import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import request from "@/lib/api/handler";
import { toast } from "sonner";

import { ApproveVideoSchema, UploadVideoSchema } from "./schema";
import { ProfileVideo } from "@/redux/features/candidateProfileSlice";
import { Candidate } from "@/queries/candidate/my-candidate";
import { QueryKeys } from "@/queries/utils";

const useCandidateProfileApproveVideosMutation = (
  userId: string,
  onSuccess?: (data: ProfileVideo[]) => void,
) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  return useMutation({
    mutationFn: async (data: ApproveVideoSchema) => {
      const token = await getAccessTokenSilently();
      const body = JSON.stringify(data);

      const response: ProfileVideo[] = await request({
        method: "PATCH",
        token,
        path: `/api/candidate/${userId}/videos`,
        body,
      });

      return response;
    },
    onError() {
      toast.error(`Failed to update videos`);
    },
    onSuccess(data) {
      const prev = qc.getQueryData<Candidate>(QueryKeys.myCandidate);

      // If there is existing data, update it
      if (prev) {
        qc.setQueryData(QueryKeys.myCandidate, {
          ...prev,
          videos: data,
        });
      } else {
        // If the entry couldn't be found, invalidate and fetch new data
        void qc.invalidateQueries({
          queryKey: QueryKeys.myCandidate,
        });
      }

      toast.success("Profile videos updated!");

      onSuccess?.(data);
    },
  });
};

const useCandidateProfileUploadVideoMutation = (
  userId: string,
  onSuccess?: (data: ProfileVideo[]) => void,
) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  return useMutation({
    mutationFn: async (data: UploadVideoSchema) => {
      const token = await getAccessTokenSilently();

      const body = new FormData();
      body.append("video", data.video);
      body.append("title", data.title);
      body.append("summary", data.summary);

      const response: ProfileVideo[] = await request({
        method: "POST",
        token,
        path: `/api/candidate/${userId}/videos`,
        body,
        contentType: "application/x-www-form-urlencoded",
      });

      return response;
    },
    onError() {
      toast.error(`Failed to upload video`);
    },
    onSuccess(data) {
      const prev = qc.getQueryData<Candidate>(QueryKeys.myCandidate);

      // If there is existing data, update it
      if (prev) {
        qc.setQueryData(QueryKeys.myCandidate, {
          ...prev,
          videos: data,
        });
      } else {
        // If the entry couldn't be found, invalidate and fetch new data
        void qc.invalidateQueries({
          queryKey: QueryKeys.myCandidate,
        });
      }

      toast.success("Profile video uploaded!");

      onSuccess?.(data);
    },
  });
};

export {
  useCandidateProfileApproveVideosMutation,
  useCandidateProfileUploadVideoMutation,
};
